// Shared LensesV2 widget helpers + constants.

// Signal × Topic co-occurrence sides — the Signals subTheme vs thematic-role
// subThemes minus signals & features. Stable subTheme slugs only. Shared so the
// Signals + Topics widgets issue an IDENTICAL cooccurrence query → one cached
// fetch (react-query dedupes by serialized body).
export const SIGNAL_TOPIC_SIDES = {
  sideA: { subThemeTagIds: ["signals"] },
  sideB: { taxonomyRoles: ["thematic"], excludeSubThemeTagIds: ["signals", "features"] },
};

// Cooccurrence params shared by ALL LensesV2 widgets: we want the FULL, unfiltered
// pair set — not a top-N slice. The BE (`mostDiscussed` view) applies NO count/
// strength/overlap floors; its only cut is `filtered.slice(0, limit)`. So we set a
// limit high enough that curated tag pairs are never truncated, and pass
// removeNoiseFilters to keep "unfiltered" explicit even if the view ever changes.
export const COOCCURRENCE_FULL = { limit: 100000, removeNoiseFilters: true };

// react-json-view props for the raw-JSON debug blocks.
export const JSON_PROPS = {
  collapsed: 2,
  displayDataTypes: false,
  displayObjectSize: false,
  enableClipboard: false,
  name: false,
  collapseStringsAfterLength: 60,
  style: { fontSize: 12 },
};

// Period-over-period trend: current total vs the previous same-length window.
// v1 parity (lenses pipeline trendCalculator.computeTrend): no prior period →
// "new" (or "stable" at zero); else compare % change against an EXCLUSIVE
// ±threshold. `thresholdPct` is per-widget — each widget passes its own from its
// CONFIG (default 20, matching v1's TREND_THRESHOLD_PCT).
export const computeTrend = (current, prev, thresholdPct = 20) => {
  if (!prev) return { trend: current ? "new" : "stable", changePercent: current ? 100 : 0 };
  const changePercent = Math.round(((current - prev) / prev) * 100);
  const trend =
    changePercent > thresholdPct ? "rising" : changePercent < -thresholdPct ? "declining" : "stable";
  return { trend, changePercent };
};

// aggrMetricsAcrossDimensions rows ([{ value, count }]) → { [tagId]: count }.
export const toTagCountMap = (rows) =>
  Object.fromEntries((rows || []).map((r) => [String(r.value), r.count]));

// Restrict a base findQuery to annotations that carry ANY of the given tag _ids —
// same shape buildIssueFindQuery uses for dissatisfaction signals, generalised so
// the Feature widget (and others) can scope a query to a tag (or set) by _id. _id
// is the canonical join key, NOT the tagId slug. No ids → the base query unchanged.
export const buildTagScopedFindQuery = (baseFindQuery, tagIds) => {
  const ids = (tagIds || []).filter(Boolean).map(String);
  if (!ids.length) return baseFindQuery || null;
  return { ...(baseFindQuery || {}), "annotations.tags.tags.data._id": { $in: ids } };
};

// Round parts to integer percentages that sum to EXACTLY 100 (largest-remainder
// method). Rounding each share independently (Math.round per part) can total 99
// or 101; this floors all, then hands the leftover point(s) to the parts with
// the largest fractional remainder. Returns an int[] aligned to `parts`.
export const toPercentages = (parts, total) => {
  if (!total) return parts.map(() => 0);
  const exact = parts.map((v) => (v / total) * 100);
  const out = exact.map((x) => Math.floor(x));
  let left = 100 - out.reduce((a, b) => a + b, 0);
  exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac)
    .forEach(({ i }) => {
      if (left > 0) {
        out[i] += 1;
        left -= 1;
      }
    });
  return out;
};
